import {
    GATE_COVER_PANEL_POSITION, GATE_MOUNT_TYPE,
    GATE_TYPE,
    PANEL_FOOTER_THEME,
    PANEL_TEST_ID,
    PANEL_TYPE,
    PANEL_VARIANT
    , PANEL_FRAME, SOCIAL_APPS
} from "./Constants";
import {asArray} from "./utils/Utils";

export const CREATE_PG_DEFAULT_OPTIONS = {
    // This must be specified. Supported values are available in the
    // PANEL_TYPE enum.
    type: PANEL_TYPE.custom,

    variant: PANEL_VARIANT.plain,

    // id of the subscription offer(s). Defaults to null. Ignored if type
    // is not "subscribe".
    subscriptionOffer: null,

    // id of the donation offer(s). Defaults to "auto". Ignored if type is
    // not "donation".
    donationOffer: "auto",

    // id of the newsletter(s). Defaults to null (all public newsletters are displayed).
    // Ignored if type is not "newsletter".
    newsletter: null,

    // A list of tabs to display in the Inline User Dashboard. The default depends on
    // what functionality the merchant has enabled.
    dashboardTabs: null,

    // A list of social providers to enable on the authentication page for this panel.
    // Besides the default options below, we also support CDS for merchants with CDS
    // subscriptions/donations backend.
    authSocialProviders: [SOCIAL_APPS.Facebook, SOCIAL_APPS.Google, SOCIAL_APPS.Twitter, SOCIAL_APPS.LinkedIn],

    // Details about the payment. Required if type is "payment".
    // Ignored if type is not "payment".
    // Typescript format of the object:
    // paymentDetails: {
    //   resourceId: String,
    //   amount?: Number,
    //   amountInSubunits?: Number,
    //   comment?: Object,
    // }
    paymentDetails: null,

    // URL of the logo to display at the top of the panel. If this is not
    // provided, no logo is displayed. The URL must be an absolute path
    // to a HTTPS resource.
    logoSrc: null,

    // The ALT text to be applied to the logo image. This is ignored if
    // logoSrc is not provided.
    logoAlt: "",

    // Style of the logo displayed at the top of the panel. This is ignored
    // if logoSrc is not provided.
    logoStyle: {},

    // Text displayed at the top of the panel. If this is left null, the
    // default differ based on panel type, subscription/donation offer etc.
    text: null,

    // Base style of the text section. It also supports sub-selectors.
    textStyle: {},

    // Some panels have titles that can be overwritten.
    title: null,

    // Base style of the login message section. It also supports sub-selectors.
    loginMessageStyle: {},

    // Text displayed in the panel button. If this is left null, the
    // default differ based on panel type, subscription/donation offer etc.
    buttonText: null,

    // Base style of the button. It also supports the ":hover" key.
    actionButtonStyle: {},

    // The theme of the Blink footer. Supported values are in the PANEL_FOOTER_THEME enum.
    footerTheme: PANEL_FOOTER_THEME.default,

    // Array of font faces. The format of a font face object is:
    /*{
        family: String,
        src: String,
        style?: String,
        weight?: Number
        unicodeRange?: String,
    }*/
    fonts: [],

    // Overrides for Stem's Theme properties.
    theme: {},

    // Overrides for the Messages object values.
    messages: {},

    // Key-value metadata to be saved for conversions (e.g. newsletter
    // registrations, donations, subscriptions etc.)
    metadata: {},

    content: null,

    context: null,

    frame: PANEL_FRAME.logo,

    // Whether to display this panel as a view-only element. This will render the panel in
    // the same way, but will make it unresponsive to user actions such as click or hover.
    // Useful for previewing how the PG would look in production.
    viewOnly: false,

    // Supported values are available in the GATE_TYPE enum.
    gateType: GATE_TYPE.null,

    // Extra style to apply to the gate element itself.
    customStyle: {},

    // Allow this gate to be expelled by user action. A cancel button will
    // appear in the top-right corner of the gate if this is enabled.
    cancellable: true,

    // Extra style to apply to the cancel button.
    cancelButtonCustomStyle: {},

    // Height (in px) of the preview area. Only applicable for type
    // "preview".
    previewHeight: 200,

    // Enable a fade-out effect between the content and the panel. Only
    // applicable for type "preview".
    previewFadeOut: true,

    // Height (in px) of the fade-out zone of the preview area. Only
    // applicable for type "preview", if "previewFadeOut" is enabled.
    previewFadeOutHeight: 100,

    // Color of the bottom of the fade-out area. Only applicable for type
    // "preview", if "previewFadeOut" is enabled.
    previewFadeOutToColor: "white",

    // Color of the cover effect of the gate. Only applicable for type
    // "cover".
    coverColor: "rgba(255, 255, 255, 0.9)",

    // Position of the panel with regards to the gate (which covers the
    // whole area of the content in this case). Only applicable for type
    // "cover".
    coverPanelPosition: GATE_COVER_PANEL_POSITION.center,

    mountType: GATE_MOUNT_TYPE.lastChild,

    scrimClickCollapse: true,

    // TODO: Add scrimBlockScroll

    // Callbacks

    onMount: null,

    onFlowCheckpoint: null,

    onFlowSuccess: null,

    onFlowFinish: null,
};

// New panels created from the dashboard have a single panel type with non-null
// options, so we can infer the panel type for most panels.
function getUniquePanelType(merchantPGOptions) {
    const availablePanelTypes = Object.keys(merchantPGOptions || {});
    return availablePanelTypes.length === 1 ? availablePanelTypes[0] : null;
}

// Given the "panelOptions" from a call to blinkSDK.createPG(),
// this function returns the options that are actually used by the PGModule to
// create the PG itself, after going through all the layers of defaults.
export function composePGOptions(givenPanelOptions, merchantDefaultPGOptions, merchantPGOptions = null, pgId = null) {
    const type = givenPanelOptions.type || getUniquePanelType(merchantPGOptions) || PANEL_TYPE.custom;
    const gateType = givenPanelOptions.gateType || GATE_TYPE.null;

    // We are only interested in the options for this panel type.
    const merchantBasePanelOptions = merchantDefaultPGOptions && merchantDefaultPGOptions[PANEL_TYPE.custom];
    const merchantPanelOptions = merchantDefaultPGOptions && merchantDefaultPGOptions[type];
    // If the PG has a unique panel type, even if it doesn't match the one requested, we
    // should still inherit from those options. If the type is not unique, then it's a
    // legacy panel and we inherit from the same type as the one in givenPanelOptions.
    merchantPGOptions = merchantPGOptions && merchantPGOptions[getUniquePanelType(merchantPGOptions) || type];

    const panelOptions = {
        fonts: [],
        theme: {},
        messages: {},
        metadata: {},
        context: [],
    };
    const apply = (options) => {
        if (options) {
            // Fonts, theme constants, messages, metadata and contexts are merged between the options.
            Object.assign(panelOptions, options, {
                fonts: [
                    ...panelOptions.fonts,
                    ...(options.fonts || []),
                ],
                theme: {
                    ...panelOptions.theme,
                    ...options.theme,
                },
                messages: {
                    ...panelOptions.messages,
                    ...options.messages,
                },
                metadata: {
                    ...panelOptions.metadata,
                    ...options.metadata,
                },
                context: [
                    ...panelOptions.context,
                    ...asArray(options.context || []),
                ],
            });
        }
    };
    apply(CREATE_PG_DEFAULT_OPTIONS);
    apply(gateType === GATE_TYPE.null ? {
        mountType: GATE_MOUNT_TYPE.replaceChildren
    } : {});
    apply(merchantBasePanelOptions && merchantBasePanelOptions[GATE_TYPE.null]);
    apply(merchantBasePanelOptions && merchantBasePanelOptions[gateType]);
    apply(merchantPanelOptions && merchantPanelOptions[GATE_TYPE.null]);
    apply(merchantPanelOptions && merchantPanelOptions[gateType]);
    apply(merchantPGOptions && merchantPGOptions[GATE_TYPE.null]);
    apply(merchantPGOptions && merchantPGOptions[gateType]);
    apply(givenPanelOptions);
    apply({
        testId: PANEL_TEST_ID[type],
        type,
        gateType,
        pgId,
    });
    return panelOptions;
}
